/** XeAgents.ai**/
/**
 * The shapes the customize page and its sections share.
 *
 * Kept apart from the components so a section can be read without pulling in the
 * page, and so the AI-provenance union has one definition - it is the thing every
 * generated field is judged against.
 */

export interface TemplateVariable {
  key: string;
  description: string;
  example: string;
  /** Filled from Agent settings at call time, never here. */
  fromAgent?: boolean;
  /** Filled per contact at call time, never here. */
  fromContact?: boolean;
  /** Empty degrades to generic copy rather than a literal {{token}}. */
  hasFallback?: boolean;
  /** No fallback and nothing fills it later, so the flow cannot be created without it. */
  required?: boolean;
}

export interface ScriptLine {
  label: string;
  message: string;
}

export interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  isTemplate: boolean;
  nodeCount: number;
  preview: string[];
  category?: string;
  openingLine?: string;
  scriptPreview?: ScriptLine[];
  outcomes?: string[];
  requiredVariables?: TemplateVariable[];
}

/**
 * Where a piece of content came from.
 *
 * The distinction that matters is `edited`: it is what stops a regeneration
 * silently replacing somebody's own words, and what the "Edited by you" label
 * reports. `generating` is held here rather than in a separate boolean so a
 * field cannot be both generating and generated at once.
 */
export type ContentOrigin = 'empty' | 'generating' | 'ai' | 'edited';

export interface AIContentState {
  value: string;
  origin: ContentOrigin;
}

export const emptyContent = (): AIContentState => ({ value: '', origin: 'empty' });

export type SalesContentField = 'valueProposition' | 'keyFeatures' | 'objections';

export interface DetectedProduct {
  name: string;
  url: string;
}

/** The website read, as the sidebar and the Step 2 panel both report it. */
export type ExtractionStage =
  | { status: 'idle' }
  | { status: 'analyzing'; step: number }
  | { status: 'choosing_product'; products: DetectedProduct[]; siteName: string }
  | { status: 'done'; scope: 'product' | 'company'; pagesRead: string[]; productName?: string }
  | { status: 'failed'; message: string };

/**
 * The five things the analysis reports doing, in order.
 *
 * Shown as a checklist rather than a spinner because reading a site takes long
 * enough that a bare spinner reads as a hang, and because naming the steps is
 * what makes the extraction legible - the user can see that objections came from
 * their own page rather than from nowhere.
 */
export const ANALYSIS_STEPS = [
  'Reading product information',
  'Identifying company information',
  'Understanding product benefits',
  'Finding key features',
  'Identifying common objections',
] as const;

/**
 * Who is being called.
 *
 * Three answers, all required, because they are no longer extras: Industry is
 * what Target Audience is built from, and Target Audience is spoken aloud in
 * the greeting and the pitch. A blank one is a sentence with a hole in it.
 *
 * It was six fields and all optional. "What hurts today" and a free-text notes
 * box went because they asked people to write the brief twice - the website
 * read and the three answers below already say who this is for - and a field
 * nobody needs is a field that gets filled with something untrue.
 *
 * This steers ANGLE and VOCABULARY only. It is never a source of facts about the
 * product; the server prompt is explicit about that, because "write it for
 * lenders" is otherwise an invitation to invent a compliance feature and say it
 * out loud under the customer's own name.
 */
export interface IcpProfile {
  industry: string;
  /** Free text, required when industry is "Other". */
  industryOther: string;
  companySize: string;
  region: string;
  /** Which side of the phone the product handles, so the pitch covers what it really does. Empty = not said. */
  callScope: CallScope;
}

export type CallScope = '' | 'inbound' | 'outbound' | 'both';

export const CALL_SCOPES: Array<{ value: Exclude<CallScope, ''>; label: string }> = [
  { value: 'inbound', label: 'Answers inbound calls' },
  { value: 'outbound', label: 'Makes outbound calls' },
  { value: 'both', label: 'Both inbound and outbound' },
];

export const emptyIcp = (): IcpProfile => ({
  industry: '',
  industryOther: '',
  companySize: '',
  region: '',
  callScope: '',
});

export const OTHER_INDUSTRY = 'Other';

/** Broad enough to cover the verticals the templates already ship for. */
export const INDUSTRIES = [
  'Banking & Financial Services',
  'NBFC & Lending',
  'Insurance',
  'Healthcare & Clinics',
  'Real Estate',
  'SaaS & Technology',
  'E-commerce & Retail',
  'Education & EdTech',
  'Travel & Hospitality',
  'Logistics & Supply Chain',
  'Manufacturing',
  'Professional Services',
  'Staffing & Recruitment',
  'Telecom',
  'Automotive',
  'Energy & Utilities',
  'Government & Public Sector',
  'Non-profit',
  OTHER_INDUSTRY,
] as const;

export const COMPANY_SIZES = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1,000',
  '1,000+',
] as const;

/**
 * Markets rather than countries.
 *
 * Region only steers tone and idiom - what a caller in Riyadh expects an
 * opening line to sound like versus one in Manchester - so a list of two
 * hundred countries would be a worse answer to the same question, and a free
 * text box was worse still: it accepted "asdf" and fed it to the writer.
 */
export const REGIONS = [
  'Global',
  'India',
  'United States',
  'Canada',
  'United Kingdom & Ireland',
  'Europe',
  'Middle East & GCC',
  'Africa',
  'Australia & New Zealand',
  'Southeast Asia',
  'Japan & South Korea',
  'Greater China',
  'Latin America',
] as const;

/** The industry as it should be read: the free-text one when "Other" is picked. */
export function resolvedIndustry(icp: IcpProfile): string {
  const industry = icp.industry === OTHER_INDUSTRY ? icp.industryOther : icp.industry;
  return String(industry || '').trim();
}

/** The profile as the server wants it: resolved, trimmed, empties dropped. */
export function icpForRequest(icp: IcpProfile): Record<string, string> {
  const entries: Array<[string, string]> = [
    ['industry', resolvedIndustry(icp)],
    ['companySize', icp.companySize],
    ['region', icp.region],
    ['callScope', icp.callScope],
  ];
  return Object.fromEntries(entries.map(([k, v]) => [k, String(v || '').trim()]).filter(([, v]) => v));
}

/** Has the user told us anything about who they are calling? */
export function hasIcp(icp: IcpProfile): boolean {
  return Object.keys(icpForRequest(icp)).length > 0;
}

/**
 * The audience line this profile implies - spoken verbatim as
 * {{target_audience}} in the greeting and the pitch.
 *
 * There used to be a Target Audience box next to the Industry dropdown, and a
 * "use this" button that copied one into the other. Two fields asking the same
 * question, where leaving the second blank put a hole in a line the agent says
 * out loud. Industry is now the single answer and this derives the sentence
 * from it.
 */
export function audienceFor(icp: IcpProfile): string {
  const industry = resolvedIndustry(icp);
  if (!industry) return '';
  // "healthcare & clinics companies" is not something anyone says; these read as people say them
  if (industry === 'Healthcare & Clinics') return 'clinics and healthcare practices';
  return `${industry.toLowerCase()} companies`;
}
